import http from "@/utils/http-common"

const DEFAULT_DOWNLOAD_FILENAME = 'download'
const API_PREFIX_PATTERN = /^\/?api\/v0\//

function getHeader(headers, name) {
  if (!headers) return ''
  const lowerName = name.toLowerCase()
  const key = Object.keys(headers).find(headerName => headerName.toLowerCase() === lowerName)
  return key ? headers[key] : ''
}

function decodeFilename(value) {
  if (!value) return ''

  const rawValue = value
    .trim()
    .replace(/^UTF-8''/i, '')

  const unwrapped = unwrapBytesLiteral(rawValue)
  const normalized = unwrapped.value
    .replace(/^['"]|['"]$/g, '')
    .replace(/\\x/g, '%')

  try {
    return normalizeDecodedFilename(decodeURIComponent(normalized), unwrapped.isBytesLiteral)
  } catch (error) {
    try {
      return normalizeDecodedFilename(decodeURI(normalized), unwrapped.isBytesLiteral)
    } catch (decodeError) {
      return normalizeDecodedFilename(normalized, unwrapped.isBytesLiteral)
    }
  }
}

function unwrapBytesLiteral(value) {
  const match = value.match(/^b(['"])(.*)\1$/i)
  return match
    ? { value: match[2], isBytesLiteral: true }
    : { value, isBytesLiteral: false }
}

function normalizeDecodedFilename(filename, isBytesLiteral = false) {
  if (!filename) return ''

  const duplicateExtension = filename.match(/_([A-Za-z0-9]+)\.\1$/)
  if (isBytesLiteral && duplicateExtension) {
    return `${filename.slice(0, duplicateExtension.index)}.${duplicateExtension[1]}`
  }

  return filename
}

export function getDownloadFilename(response, fallbackFilename = DEFAULT_DOWNLOAD_FILENAME) {
  const disposition = getHeader(response && response.headers, 'content-disposition')
  if (!disposition) return fallbackFilename

  const encodedFilename = disposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i)
  if (encodedFilename && encodedFilename[1]) {
    return decodeFilename(encodedFilename[1]) || fallbackFilename
  }

  const plainFilename = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i)
  if (plainFilename && plainFilename[1]) {
    return decodeFilename(plainFilename[1]) || fallbackFilename
  }

  return fallbackFilename
}

export function isJsonBlobResponse(response) {
  const data = response && response.data
  const contentType = getHeader(response && response.headers, 'content-type')

  return data instanceof Blob && (
    data.type === 'application/json' ||
    contentType.indexOf('application/json') !== -1
  )
}

function readBlobAsText(blob) {
  if (blob && typeof blob.text === 'function') return blob.text()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

export async function parseBlobError(response) {
  if (!isJsonBlobResponse(response)) return null

  const text = await readBlobAsText(response.data)
  if (!text) return null

  try {
    return JSON.parse(text)
  } catch (error) {
    return { detail: text }
  }
}

export function downloadBlob(blob, filename = DEFAULT_DOWNLOAD_FILENAME) {
  const downloadData = blob instanceof Blob ? blob : new Blob([blob])
  const fileObjectUrl = window.URL.createObjectURL(downloadData)
  const link = document.createElement('a')

  link.href = fileObjectUrl
  link.style.display = 'none'
  link.download = filename || DEFAULT_DOWNLOAD_FILENAME
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(fileObjectUrl)
}

export async function downloadBlobResponse(response, options = {}) {
  const errorData = await parseBlobError(response)
  if (errorData) {
    const error = new Error(errorData.detail || errorData.message || '다운로드에 실패했습니다.')
    error.response = {
      status: response.status,
      headers: response.headers,
      data: errorData,
    }
    throw error
  }

  const blob = response.data instanceof Blob ? response.data : new Blob([response.data])
  const filename = options.preferFallbackFilename
    ? getFallbackDownloadFilename(null, options.fallbackFilename)
    : getDownloadFilename(response, options.fallbackFilename)
  downloadBlob(blob, filename)
  return filename
}

export function normalizeAuthorizedDownloadUrl(url) {
  if (!url) return ''

  const normalizedUrl = String(url).trim()
  if (!normalizedUrl) return ''

  if (/^https?:\/\//i.test(normalizedUrl)) return normalizedUrl
  return normalizedUrl
    .replace(API_PREFIX_PATTERN, '')
    .replace(/^\/+/, '')
}

export function buildAuthorizedDownloadUrl(db, id, field) {
  if (!db || !id || !field) return ''
  return `annotation/download-file/${db}/${id}/${field}/`
}

export function getAuthorizedFileDownloadUrl(record, options = {}) {
  const db = options.db || 'multiTableData'
  const field = options.field || 'file'
  const downloadUrlKey = options.downloadUrlKey || `${field}_download_url`

  return normalizeAuthorizedDownloadUrl(
    options.url ||
    (record && (record[downloadUrlKey] || record.download_url)) ||
    buildAuthorizedDownloadUrl(db, record && (record.id || record.pk), field)
  )
}

export function getFallbackDownloadFilename(record, fallbackFilename = DEFAULT_DOWNLOAD_FILENAME) {
  return (
    fallbackFilename ||
    (record && (record.name || record.file_name)) ||
    DEFAULT_DOWNLOAD_FILENAME
  )
}

export async function downloadAuthorizedFile(options = {}) {
  const url = normalizeAuthorizedDownloadUrl(
    options.url || buildAuthorizedDownloadUrl(options.db, options.id, options.field)
  )

  if (!url) {
    throw new Error('다운로드 URL을 확인할 수 없습니다.')
  }

  const response = await http.get(url, { responseType: 'blob' })
  return downloadBlobResponse(response, {
    fallbackFilename: getFallbackDownloadFilename(null, options.fallbackFilename),
    preferFallbackFilename: options.preferFallbackFilename,
  })
}

export async function downloadAuthorizedRecordFile(record, options = {}) {
  return downloadAuthorizedFile({
    db: options.db || 'multiTableData',
    id: record && (record.id || record.pk),
    field: options.field || 'file',
    url: getAuthorizedFileDownloadUrl(record, options),
    fallbackFilename: getFallbackDownloadFilename(record, options.fallbackFilename),
    preferFallbackFilename: options.preferFallbackFilename,
  })
}

export async function createAuthorizedObjectUrl(url) {
  const normalizedUrl = normalizeAuthorizedDownloadUrl(url)
  if (!normalizedUrl) {
    return {
      objectUrl: '',
      revoke() {
        return undefined
      },
    }
  }

  const response = await http.get(normalizedUrl, { responseType: 'blob' })
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data])
  const objectUrl = window.URL.createObjectURL(blob)

  return {
    objectUrl,
    revoke() {
      window.URL.revokeObjectURL(objectUrl)
    },
  }
}

const authorizedImageObjectUrlCache = new Map()

export function getCachedAuthorizedImageSrc(url) {
  const normalizedUrl = normalizeAuthorizedDownloadUrl(url)
  if (!normalizedUrl) return ''

  const cached = authorizedImageObjectUrlCache.get(normalizedUrl)
  return cached && cached.objectUrl ? cached.objectUrl : ''
}

export async function getCachedAuthorizedObjectUrl(url) {
  const normalizedUrl = normalizeAuthorizedDownloadUrl(url)
  if (!normalizedUrl) {
    return {
      normalizedUrl: '',
      objectUrl: '',
    }
  }

  const cached = authorizedImageObjectUrlCache.get(normalizedUrl)
  if (cached) {
    if (cached.objectUrl) {
      return {
        normalizedUrl,
        objectUrl: cached.objectUrl,
      }
    }
    if (cached.promise) return cached.promise
  }

  const cacheEntry = {
    objectUrl: '',
    revoke: null,
    promise: null,
    released: false,
  }

  cacheEntry.promise = createAuthorizedObjectUrl(normalizedUrl)
    .then((result) => {
      if (cacheEntry.released) {
        result.revoke()
        return {
          normalizedUrl,
          objectUrl: '',
        }
      }

      cacheEntry.objectUrl = result.objectUrl
      cacheEntry.revoke = result.revoke
      cacheEntry.promise = null
      return {
        normalizedUrl,
        objectUrl: result.objectUrl,
      }
    })
    .catch((error) => {
      authorizedImageObjectUrlCache.delete(normalizedUrl)
      throw error
    })

  authorizedImageObjectUrlCache.set(normalizedUrl, cacheEntry)
  return cacheEntry.promise
}

export async function preloadAuthorizedImages(urls = [], options = {}) {
  const uniqueUrls = Array.from(new Set(
    urls
      .map(url => normalizeAuthorizedDownloadUrl(url))
      .filter(Boolean)
  ))

  if (options.force) releaseAuthorizedImageUrls(uniqueUrls)

  return Promise.all(uniqueUrls.map(url => (
    getCachedAuthorizedObjectUrl(url)
      .then(result => ({ ...result, status: 'fulfilled' }))
      .catch(error => ({ normalizedUrl: url, objectUrl: '', status: 'rejected', error }))
  )))
}

export function releaseAuthorizedImageUrls(urls = []) {
  const uniqueUrls = Array.from(new Set(
    urls
      .map(url => normalizeAuthorizedDownloadUrl(url))
      .filter(Boolean)
  ))

  uniqueUrls.forEach((url) => {
    const cached = authorizedImageObjectUrlCache.get(url)
    if (cached) cached.released = true
    if (cached && cached.revoke) cached.revoke()
    authorizedImageObjectUrlCache.delete(url)
  })
}
